/**
 * Iter128b — Field Operations Command Center (Admin).
 *
 * Live dashboard of active field ops + investigator timers. Shows:
 *  - Active timers (who started, when, which case)
 *  - Live GPS pins for every investigator with a recent position
 *  - Recent field log entries per active operation
 *
 * Admin-only. Polls every 30s.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import {
  Radio, Clock, MapPin, RefreshCw, Activity, Users, PlayCircle, Timer, UserCheck, AlertTriangle,
} from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
// Iter132 — keep this frozen reference identical to CaseOperationsMap.
// `useJsApiLoader` keys its singleton on the libraries array, and a
// fresh literal on every render would trip "Loader must not be called
// again with different options".
const GOOGLE_MAPS_LIBRARIES = ['places', 'drawing'];

const mapContainerStyle = { width: '100%', height: '520px', borderRadius: 8 };
const defaultCenter = { lat: 32.7767, lng: -96.7970 };

function fmtDuration(startIso) {
  if (!startIso) return '—';
  const start = new Date(startIso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - start) / 1000));
  const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const sec = String(s % 60).padStart(2, '0');
  return `${h}:${m}:${sec}`;
}

export default function FieldCommandCenter() {
  const { user, isAdmin } = useAuth();
  if (!user) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <FieldDispatchPanel />;
}

// Iter132 — reusable admin dispatch panel.  Rendered standalone at
// /field-command and embedded inside the Field Map (CaseOperationsMap)
// for admins so dispatch + telemetry live alongside tactical planning.
export function FieldDispatchPanel({ embedded = false } = {}) {
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  // Iter132 fix: use the SAME libraries list as CaseOperationsMap so
  // the @react-google-maps/api singleton loader never gets two
  // different option sets (which white-screens with
  // "Loader must not be called again with different options").
  const { isLoaded: _loaderReady } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const isLoaded = embedded ? false : _loaderReady;

  const [investigators, setInvestigators] = useState([]);
  const [activeTimers, setActiveTimers] = useState([]);
  const [ops, setOps] = useState([]);
  const [liveFeed, setLiveFeed] = useState([]);
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [inactivityMinutes, setInactivityMinutes] = useState(10);
  const [selected, setSelected] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);

  // Iter133 — reverse-geocode the hovered/selected investigator's live pin
  // so the InfoWindow shows their physical address, not just lat/lng.
  const resolveAddress = useCallback((lat, lng) => {
    if (!window.google?.maps?.Geocoder) { setSelectedAddress(null); return; }
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          setSelectedAddress(results[0].formatted_address);
        } else {
          setSelectedAddress(null);
        }
      });
    } catch { setSelectedAddress(null); }
  }, []);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [invRes, timerRes, opsRes, feedRes, settingsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/investigators/tracking`, { headers: H }),
        fetch(`${BACKEND_URL}/api/time-entries/admin/active`, { headers: H }).catch(() => null),
        fetch(`${BACKEND_URL}/api/field-operations?status=IN_PROGRESS`, { headers: H }).catch(() => null),
        fetch(`${BACKEND_URL}/api/field-assignments/active/live-feed?limit=25`, { headers: H }).catch(() => null),
        fetch(`${BACKEND_URL}/api/admin/field-ops-settings`, { headers: H }).catch(() => null),
      ]);
      if (invRes && invRes.ok) {
        const d = await invRes.json();
        setInvestigators(Array.isArray(d) ? d : (d.investigators || d.items || []));
      }
      if (timerRes && timerRes.ok) {
        const d = await timerRes.json();
        setActiveTimers(Array.isArray(d) ? d : (d.timers || d.items || d.active || []));
      }
      if (opsRes && opsRes.ok) {
        const d = await opsRes.json();
        setOps(Array.isArray(d) ? d : (d.operations || d.items || []));
      }
      if (feedRes && feedRes.ok) {
        const d = await feedRes.json();
        setLiveFeed(d.events || []);
      }
      if (settingsRes && settingsRes.ok) {
        const d = await settingsRes.json();
        setInactivityMinutes(d.inactivity_minutes || 10);
      }
      // Fetch active assignments too so we can show inactivity alerts
      try {
        const r = await fetch(`${BACKEND_URL}/api/field-assignments/active`, { headers: H });
        if (r.ok) {
          const d = await r.json();
          setActiveAssignments(Array.isArray(d) ? d : []);
        }
      } catch {}
    } catch (e) {
      toast({ title: 'Refresh failed', description: e.message, variant: 'destructive' });
    } finally { setRefreshing(false); }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);
  useEffect(() => { const i = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(i); }, []);

  const pinned = useMemo(
    () => investigators.filter(i => i.latitude && i.longitude),
    [investigators]
  );

  const mapCenter = pinned[0]
    ? { lat: pinned[0].latitude, lng: pinned[0].longitude }
    : defaultCenter;

  return (
    <div className="space-y-6" data-testid="field-command-center">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-red-500/20 border border-red-500/40 flex items-center justify-center">
            <Radio className="w-6 h-6 text-red-300 animate-pulse" />
          </div>
          <div>
            <div className="text-xs text-red-300 uppercase tracking-wider font-mono">Admin · Field Ops</div>
            <h1 className="text-2xl font-bold text-white">Field Operations Command Center</h1>
            <p className="text-slate-400 text-sm">Live view of every active investigator, timer, and field entry.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => window.open('/admin/edit-approvals', '_self')} className="text-slate-300" data-testid="fcc-edit-approvals">
            <UserCheck className="w-4 h-4 mr-1" /> Edit Approvals
          </Button>
          <Button variant="ghost" onClick={load} className="text-slate-300" data-testid="fcc-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Active Timers', v: activeTimers.length, icon: Timer, color: 'text-emerald-300', bg: 'bg-emerald-500/20' },
          { label: 'Live Pins', v: pinned.length, icon: MapPin, color: 'text-sky-300', bg: 'bg-sky-500/20' },
          { label: 'In-Progress Ops', v: ops.length, icon: PlayCircle, color: 'text-amber-300', bg: 'bg-amber-500/20' },
          { label: 'Total Investigators', v: investigators.length, icon: Users, color: 'text-slate-300', bg: 'bg-slate-500/20' },
        ].map(s => (
          <Card key={s.label} className="bg-[#131A2A] border-[#334155]">
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-md ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider">{s.label}</div>
                <div className="text-xl font-bold text-white">{s.v}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Map */}
        <Card className="bg-[#131A2A] border-[#334155] lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-white">Live Locations</CardTitle></CardHeader>
          <CardContent className="p-0">
            {embedded ? (
              <div className="p-4 text-slate-400 text-sm">
                Live map rendered below in the tactical satellite view.
                <span className="block text-xs text-slate-500 mt-1">
                  Open the standalone <a href="/field-command" className="text-blue-400 hover:underline">Field Ops Command</a> page for the dedicated dispatch map.
                </span>
              </div>
            ) : !isLoaded ? (
              <div className="p-6 text-slate-400 text-sm">Loading map…</div>
            ) : (
              <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={pinned.length ? 11 : 6}>
                {pinned.map(inv => (
                  <Marker
                    key={inv.id}
                    position={{ lat: inv.latitude, lng: inv.longitude }}
                    onClick={() => { setSelected(inv); setSelectedAddress(null); resolveAddress(inv.latitude, inv.longitude); }}
                    onMouseOver={() => { setSelected(inv); setSelectedAddress(null); resolveAddress(inv.latitude, inv.longitude); }}
                    label={{ text: (inv.name || 'I')[0], color: '#fff', fontWeight: '700' }}
                  />
                ))}
                {selected && (
                  <InfoWindow
                    position={{ lat: selected.latitude, lng: selected.longitude }}
                    onCloseClick={() => { setSelected(null); setSelectedAddress(null); }}
                  >
                    <div style={{ minWidth: 220 }} data-testid="investigator-infowindow">
                      <div style={{ fontWeight: 700 }}>{selected.name}</div>
                      <div style={{ fontSize: 11, color: '#475569' }}>{selected.tracking_status}</div>
                      <div style={{ fontSize: 11, color: '#1e293b', marginTop: 4 }}>
                        <strong>Address:</strong>{' '}
                        {selectedAddress || `${(selected.latitude || 0).toFixed(5)}, ${(selected.longitude || 0).toFixed(5)}`}
                      </div>
                      {selected.last_seen && <div style={{ fontSize: 11, marginTop: 2 }}>Last seen: {new Date(selected.last_seen).toLocaleString()}</div>}
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            )}
          </CardContent>
        </Card>

        {/* Active timers */}
        <Card className="bg-[#131A2A] border-[#334155]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white flex items-center justify-between">
              <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-300" /> Active Timers</span>
              <Badge className="bg-emerald-500/20 text-emerald-300 text-[10px]">{activeTimers.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[520px] overflow-y-auto space-y-2">
            {activeTimers.length === 0 ? (
              <div className="text-xs text-slate-500 py-4 text-center">No active timers.</div>
            ) : activeTimers.map(t => (
              <div key={t.id} className="p-2 rounded bg-slate-900/50 border border-slate-800" data-testid={`active-timer-${t.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white truncate">{t.investigator_name}</span>
                  <span className="font-mono text-emerald-300 text-sm" data-tick={tick}>{fmtDuration(t.start_time)}</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {t.case_number || t.case_id} · {(t.activity || '').replace(/_/g, ' ').toLowerCase()}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Iter132 — Live Monitoring Feed + Inactivity Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-[#131A2A] border-[#334155]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white flex items-center justify-between">
              <span className="flex items-center gap-2"><Activity className="w-4 h-4 text-sky-300" /> Live Activity Stream</span>
              <Badge className="bg-sky-500/20 text-sky-300 text-[10px]">{liveFeed.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[320px] overflow-y-auto space-y-1.5" data-testid="field-live-feed">
            {liveFeed.length === 0 ? (
              <div className="text-xs text-slate-500 py-4 text-center">No field events yet. Investigators' logs, notes, and photos appear here live.</div>
            ) : liveFeed.map(e => (
              <div key={e.id} className="p-2 rounded bg-slate-900/50 border border-slate-800 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-500 font-mono">{new Date(e.timestamp).toLocaleTimeString()}</span>
                  <Badge className="bg-slate-700 text-slate-200 text-[10px]">{e.event_type}</Badge>
                </div>
                <div className="text-slate-200 mt-0.5"><span className="text-slate-400">{e.investigator_name}:</span> {e.content || '—'}</div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">{e.case_number || e.case_id}</div>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="bg-[#131A2A] border-[#334155]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white flex items-center justify-between">
              <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-300" /> Inactivity Alerts</span>
              <span className="text-[10px] text-amber-300 font-mono">THRESHOLD · {inactivityMinutes} MIN</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[320px] overflow-y-auto space-y-1.5" data-testid="field-inactivity-list">
            {activeAssignments.filter(a => a.is_inactive).length === 0 ? (
              <div className="text-xs text-slate-500 py-4 text-center">All active assignments are healthy. No inactivity to report.</div>
            ) : activeAssignments.filter(a => a.is_inactive).map(a => (
              <div key={a.id} className="p-2 rounded bg-amber-500/5 border border-amber-500/30 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white font-semibold">{a.investigator_name || a.investigator_id}</span>
                  <Badge className="bg-amber-500/20 text-amber-300 text-[10px]">{a.inactive_minutes} MIN STALE</Badge>
                </div>
                <div className="text-slate-300 mt-0.5">{a.case_number || a.case_id} · {(a.purpose || '').replace(/_/g, ' ')}</div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">Last activity: {new Date(a.last_event_at).toLocaleTimeString()}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Active field ops with recent entries */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-white flex items-center gap-2">
            <Activity className="w-4 h-4 text-amber-300" /> Active Field Operations · Recent Entries
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ops.length === 0 ? (
            <div className="text-xs text-slate-500 py-4 text-center">No field operations in progress.</div>
          ) : (
            <div className="space-y-3">
              {ops.map(op => {
                const logs = (op.field_logs || []).slice(-3).reverse();
                return (
                  <div key={op.id} className="p-3 rounded bg-slate-900/50 border border-slate-800" data-testid={`op-${op.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="text-sm text-white font-semibold">{op.title || op.operation_type}</div>
                        <div className="text-xs text-slate-400">
                          Case {op.case_number || op.case_id} · {(op.assigned_investigator_names || []).join(', ')}
                        </div>
                      </div>
                      <Badge className="bg-amber-500/20 text-amber-300 text-[10px]">{op.status}</Badge>
                    </div>
                    {logs.length > 0 && (
                      <div className="mt-2 space-y-1 border-t border-slate-800 pt-2">
                        {logs.map(l => (
                          <div key={l.id} className="text-xs text-slate-300">
                            <span className="text-slate-500 font-mono mr-2">{new Date(l.at).toLocaleTimeString()}</span>
                            <span className="text-slate-400">{l.investigator_name}:</span> {l.note}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
